// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, Grid, InputLabel, MenuItem, Paper, Select, TextField,
  Typography,
} from '@mui/material';
import api from '../../utils/api';
import { NOT_PROVIDED } from '../../utils/formatFacturation';
import NamedConfirmation from './ConfirmationNommee';
import { today, shortDate, dayToIso } from '../../utils/dates';
import { errorMessage } from '../../utils/erreurs';
import { toFileName, downloadJson } from './commun';

// Mêmes valeurs que `models/site_closure.py` : TERM_MONTHS et NOTICE_MONTHS
// sont proposées, jamais imposées (le backend les applique si le champ est vide).
const PROPOSED_DURATION = 36;
const PROPOSED_NOTICE = 3;

const PARTIES = { etablissement: "L'établissement", vigeos: 'Vigeos' };
const REASONS = {
  non_reconduction: 'Non-reconduction', resiliation: 'Résiliation',
  manquement: 'Manquement', transfert: 'Transfert', autre: 'Autre',
};
const INSTRUCTIONS = {
  effacement: 'Effacement', restitution: 'Restitution puis effacement',
  non_recue: 'Demandée, non reçue',
};
const STATUS_DISPLAY = {
  denonce: { label: 'Dénoncé', couleur: 'warning' },
  clos: { label: 'Clos', couleur: 'error' },
  restitue: { label: 'Restitué', couleur: 'info' },
  efface: { label: 'Effacé', couleur: 'default' },
};

const FORMS = {
  recette: () => ({ recette_at: today(), term_months: String(PROPOSED_DURATION), notice_months: String(PROPOSED_NOTICE) }),
  termination: () => ({ party: 'etablissement', notified_at: today(), notice_months: '', reason: '', note: '' }),
  reversibility: () => ({ instruction: 'effacement', reference: '', received_at: today() }),
  export: () => ({ reference: '', delivered_at: today() }),
};

function toInteger(text) {
  const t = String(text ?? '').trim();
  return t === '' ? null : Number(t);
}

function Ligne({ libelle: label, valeur }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 170 }}>{label}</Typography>
      <Typography variant="body2">{valeur || NOT_PROVIDED}</Typography>
    </Box>
  );
}

/**
 * Contrat d'un établissement, tel que `pro_closure.py` le tient : recette
 * (art. 8.1), seule à faire courir l'engagement (le « Début de contrat » de
 * l'onglet Informations ne calcule rien), et fin de contrat (art. 8.7). Les
 * gestes suivent l'ordre du backend : dénonciation, instruction écrite,
 * clôture, restitution, effacement. Un refus 409 s'affiche tel quel ; clôture
 * et effacement exigent de retaper le nom de l'établissement.
 */
export default function ContractBlock({ site, onToast }) {
  const [state, setState] = useState({ chargement: true, donnees: null, erreur: '' });
  const [dialogue, setDialogue] = useState(null);   // { type, valeurs, erreur, enCours }
  const [confirmation, setConfirmation] = useState(null); // { type, erreur, enCours }

  const charger = useCallback(async () => {
    setState((e) => ({ ...e, chargement: true, erreur: '' }));
    try {
      const r = await api.get(`/pro/admin/sites/${site.id}/closure`);
      setState({ chargement: false, donnees: r.data, erreur: '' });
    } catch (e) {
      setState({ chargement: false, donnees: null, erreur: errorMessage(e, 'Contrat illisible') });
    }
  }, [site.id]);

  useEffect(() => { charger(); }, [charger]);

  const openItem = (type) => setDialogue({ type, valeurs: FORMS[type](), erreur: '', enCours: false });
  const update = (patch) => setDialogue((d) => (d ? { ...d, valeurs: { ...d.valeurs, ...patch } } : d));

  const send = async () => {
    const d = dialogue;
    if (!d) return;
    setDialogue({ ...d, enCours: true, erreur: '' });
    const v = d.valeurs;
    try {
      if (d.type === 'recette') {
        const r = await api.post(`/pro/admin/sites/${site.id}/recette`, {
          recette_at: dayToIso(v.recette_at),
          term_months: toInteger(v.term_months),
          notice_months: toInteger(v.notice_months),
        });
        setState({ chargement: false, donnees: r.data, erreur: '' });
        onToast?.('Recette enregistrée : l\'engagement court');
      } else if (d.type === 'termination') {
        const r = await api.post(`/pro/admin/sites/${site.id}/termination`, {
          party: v.party,
          notified_at: dayToIso(v.notified_at),
          notice_months: toInteger(v.notice_months),
          reason: v.reason || null,
          note: v.note.trim() || null,
        });
        setState({ chargement: false, donnees: r.data, erreur: '' });
        onToast?.('Dénonciation enregistrée');
      } else if (d.type === 'reversibility') {
        const r = await api.post(`/pro/admin/sites/${site.id}/reversibility`, {
          instruction: v.instruction,
          reference: v.reference.trim() || null,
          received_at: dayToIso(v.received_at),
        });
        setState({ chargement: false, donnees: r.data, erreur: '' });
        onToast?.('Instruction écrite enregistrée');
      } else if (d.type === 'export') {
        const r = await api.post(`/pro/admin/sites/${site.id}/export`, {
          reference: v.reference.trim() || null,
          delivered_at: dayToIso(v.delivered_at),
        });
        downloadJson(r.data, `restitution-${toFileName(site.name)}-${today()}.json`);
        onToast?.('Export produit et restitution consignée');
        charger();
      }
      setDialogue(null);
    } catch (e) {
      setDialogue({ ...d, enCours: false, erreur: errorMessage(e, 'Refusé par le serveur') });
    }
  };

  const confirmer = async ({ confirm }) => {
    const c = confirmation;
    if (!c) return;
    setConfirmation({ ...c, enCours: true, erreur: '' });
    try {
      if (c.type === 'close') {
        const r = await api.post(`/pro/admin/sites/${site.id}/close`, {});
        setState({ chargement: false, donnees: r.data, erreur: '' });
        onToast?.(`${site.name} est clos`);
      } else {
        // axios ne porte le corps d'un DELETE que dans `data`.
        const r = await api.delete(`/pro/admin/sites/${site.id}/data`, { data: { confirm } });
        downloadJson(r.data, `certificat-effacement-${toFileName(site.name)}.json`);
        onToast?.(`Données de ${site.name} effacées, certificat scellé`);
        charger();
      }
      setConfirmation(null);
    } catch (e) {
      setConfirmation({ ...c, enCours: false, erreur: errorMessage(e, 'Refusé par le serveur') });
    }
  };

  const data = state.donnees;
  const contract = data?.contrat || {};
  const dossier = data?.dossier || null;
  const closed = !!contract.cloture;
  const sealed = !!dossier?.effacement?.sceau;
  const verifiedSeal = dossier?.effacement?.sceau_verifie || null;
  const caseOpen = !!dossier && !sealed;
  const instruction = dossier?.instruction?.nature;
  const canErase = caseOpen && closed && !!instruction && instruction !== 'non_recue'
    && (instruction !== 'restitution' || !!dossier?.restitution?.date);
  const statusValue = dossier ? (STATUS_DISPLAY[dossier.statut] || { label: dossier.statut, couleur: 'default' }) : null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">
          {site.name}
          {site.city ? <Typography component="span" variant="caption" color="text.secondary"> · {site.city}</Typography> : null}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {statusValue && <Chip size="small" label={`Dossier : ${statusValue.label}`} color={statusValue.couleur} />}
          {closed && <Chip size="small" label={`Clos le ${shortDate(contract.cloture)}`} />}
        </Box>
      </Box>

      {state.chargement && !data ? (
        <CircularProgress size={18} />
      ) : state.erreur ? (
        <Alert severity="error">{state.erreur}</Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                Engagement (art. 8.1)
              </Typography>
              {!contract.recette ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Aucune recette enregistrée : l&apos;engagement ne court pas, et la fin
                  d&apos;engagement comme la date d&apos;effet d&apos;une dénonciation se
                  calculent sans point de départ.
                </Alert>
              ) : (
                <>
                  <Ligne libelle="Recette" valeur={shortDate(contract.acceptance)} />
                  <Ligne libelle="Durée d'engagement" valeur={contract.duree_engagement_mois != null ? `${contract.duree_engagement_mois} mois` : null} />
                  <Ligne libelle="Préavis" valeur={contract.preavis_mois != null ? `${contract.preavis_mois} mois` : null} />
                  <Ligne libelle="Fin d'engagement" valeur={shortDate(contract.fin_engagement)} />
                </>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                Fin de contrat (art. 8.7)
              </Typography>
              {!dossier ? (
                <Typography variant="body2" color="text.secondary">Aucun dossier de fin de contrat.</Typography>
              ) : (
                <>
                  <Ligne libelle="Dénonciation"
                    valeur={dossier.denonciation?.date
                      ? `${shortDate(dossier.denonciation.date)} par ${PARTIES[dossier.denonciation.par] || dossier.denonciation.par}`
                        + (dossier.denonciation.motif ? ` (${REASONS[dossier.denonciation.motif] || dossier.denonciation.motif})` : '')
                      : null} />
                  <Ligne libelle="Date d'effet" valeur={shortDate(dossier.denonciation?.effet)} />
                  <Ligne libelle="Instruction écrite"
                    valeur={instruction
                      ? `${INSTRUCTIONS[instruction] || instruction}${dossier.instruction.reference ? `, réf. ${dossier.instruction.reference}` : ''}`
                      : null} />
                  <Ligne libelle="Clôture"
                    valeur={dossier.cloture?.date
                      ? `${shortDate(dossier.cloture.date)}, dépose avant le ${shortDate(dossier.cloture.depose_due_avant_le)} (${dossier.cloture.actifs_sur_site ?? 0} actif(s) sur site)`
                      : null} />
                  <Ligne libelle="Restitution"
                    valeur={dossier.restitution?.date
                      ? `${shortDate(dossier.restitution.date)}, SHA-256 ${String(dossier.restitution.empreinte_sha256 || '').slice(0, 12)}…`
                      : null} />
                  <Ligne libelle="Effacement" valeur={shortDate(dossier.effacement?.date)} />
                  {sealed && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                      <Chip size="small"
                        label={verifiedSeal?.valide ? 'Certificat scellé, sceau vérifié' : `Sceau NON vérifié${verifiedSeal?.motif ? ` : ${verifiedSeal.motif}` : ''}`}
                        color={verifiedSeal?.valide ? 'success' : 'error'} />
                      <Button size="small"
                        onClick={() => downloadJson(
                          { certificat: dossier.certificat, preuve: dossier.effacement },
                          `certificat-effacement-${toFileName(site.name)}.json`,
                        )}>
                        Télécharger le certificat
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
            <Button size="small" variant="outlined" disabled={closed} onClick={() => openItem('recette')}>
              {contract.recette ? 'Corriger la recette' : 'Enregistrer la recette'}
            </Button>
            <Button size="small" variant="outlined" color="warning" disabled={closed} onClick={() => openItem('termination')}>
              {caseOpen && dossier.denonciation?.date ? 'Corriger la dénonciation' : 'Enregistrer une dénonciation'}
            </Button>
            <Button size="small" variant="outlined" disabled={!caseOpen} onClick={() => openItem('reversibility')}>
              Instruction écrite
            </Button>
            <Button size="small" variant="outlined" color="error"
              disabled={closed || !caseOpen || !dossier?.denonciation?.date}
              onClick={() => setConfirmation({ type: 'close', erreur: '', enCours: false })}>
              Clore l&apos;établissement
            </Button>
            {/* Après le sceau, les données sont effacées : un export ne
                restituerait plus rien et consignerait une restitution fausse. */}
            <Button size="small" variant="outlined" disabled={sealed} onClick={() => openItem('export')}>
              Exporter et consigner la restitution
            </Button>
            <Button size="small" variant="contained" color="error" disabled={!canErase}
              onClick={() => setConfirmation({ type: 'erase', erreur: '', enCours: false })}>
              Effacer les données
            </Button>
          </Box>
        </>
      )}

      <Dialog open={!!dialogue} onClose={() => !dialogue?.enCours && setDialogue(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogue?.type === 'recette' && `Recette contractuelle : ${site.name}`}
          {dialogue?.type === 'termination' && `Dénonciation du contrat : ${site.name}`}
          {dialogue?.type === 'reversibility' && `Instruction écrite de réversibilité : ${site.name}`}
          {dialogue?.type === 'export' && `Restitution des données : ${site.name}`}
        </DialogTitle>
        <DialogContent dividers>
          {dialogue?.erreur && <Alert severity="error" sx={{ mb: 2 }}>{dialogue.erreur}</Alert>}
          {dialogue?.type === 'recette' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Date de la recette CONTRADICTOIRE, constatée avec l&apos;établissement :
                  la fin d&apos;engagement en découle. Durée et préavis vides = valeurs du
                  contrat cadre ({PROPOSED_DURATION} et {PROPOSED_NOTICE} mois).
                </Alert>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="date" required label="Recette"
                  InputLabelProps={{ shrink: true }} value={dialogue.valeurs.recette_at}
                  onChange={(e) => update({ recette_at: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" label="Durée (mois)"
                  inputProps={{ min: 0, max: 120 }} value={dialogue.valeurs.term_months}
                  onChange={(e) => update({ term_months: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" label="Préavis (mois)"
                  inputProps={{ min: 0, max: 24 }} value={dialogue.valeurs.notice_months}
                  onChange={(e) => update({ notice_months: e.target.value })} />
              </Grid>
            </Grid>
          )}
          {dialogue?.type === 'termination' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Ouvre le dossier de fin de contrat. Préavis vide = celui du contrat ;
                  0 vaut résiliation sans préavis (art. 8.4) et rend l&apos;effet immédiat.
                  Rien n&apos;est coupé ici : la clôture est un geste distinct.
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Dénoncé par</InputLabel>
                  <Select label="Dénoncé par" value={dialogue.valeurs.party}
                    onChange={(e) => update({ party: e.target.value })}>
                    {Object.entries(PARTIES).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="date" label="Notifiée le"
                  InputLabelProps={{ shrink: true }} value={dialogue.valeurs.notified_at}
                  onChange={(e) => update({ notified_at: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="number" label="Préavis (mois)"
                  inputProps={{ min: 0, max: 24 }} value={dialogue.valeurs.notice_months}
                  onChange={(e) => update({ notice_months: e.target.value })}
                  helperText="Vide : préavis du contrat" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Motif</InputLabel>
                  <Select label="Motif" value={dialogue.valeurs.reason}
                    onChange={(e) => update({ reason: e.target.value })}>
                    <MenuItem value="">Non précisé</MenuItem>
                    {Object.entries(REASONS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Note" multiline rows={2}
                  inputProps={{ maxLength: 500 }} value={dialogue.valeurs.note}
                  onChange={(e) => update({ note: e.target.value })} />
              </Grid>
            </Grid>
          )}
          {dialogue?.type === 'reversibility' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info">
                  L&apos;article 8.7 exige un ÉCRIT de l&apos;établissement : la référence du
                  courrier, du courriel ou du PV est obligatoire, sauf pour « demandée,
                  non reçue ».
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Instruction</InputLabel>
                  <Select label="Instruction" value={dialogue.valeurs.instruction}
                    onChange={(e) => update({ instruction: e.target.value })}>
                    {Object.entries(INSTRUCTIONS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="date" label="Reçue le"
                  InputLabelProps={{ shrink: true }} value={dialogue.valeurs.received_at}
                  onChange={(e) => update({ received_at: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Référence de l'écrit"
                  inputProps={{ maxLength: 200 }} value={dialogue.valeurs.reference}
                  onChange={(e) => update({ reference: e.target.value })} />
              </Grid>
            </Grid>
          )}
          {dialogue?.type === 'export' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="warning">
                  Produit l&apos;export COMPLET de l&apos;établissement, données de santé en
                  clair, et l&apos;inscrit au dossier comme restitution remise (empreinte
                  SHA-256 conservée). Le fichier s&apos;enregistre sur ce poste : il se remet
                  à l&apos;établissement par un canal sûr, et ne se garde pas ici.
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="date" label="Remise le"
                  InputLabelProps={{ shrink: true }} value={dialogue.valeurs.delivered_at}
                  onChange={(e) => update({ delivered_at: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Référence de remise"
                  inputProps={{ maxLength: 200 }} value={dialogue.valeurs.reference}
                  onChange={(e) => update({ reference: e.target.value })}
                  helperText="Bordereau, courriel ou support" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogue(null)} disabled={dialogue?.enCours}>Annuler</Button>
          <Button variant="contained" onClick={send} disabled={dialogue?.enCours}>
            {dialogue?.enCours ? <CircularProgress size={18} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <NamedConfirmation
        ouvert={!!confirmation}
        titre={confirmation?.type === 'close' ? `Clore ${site.name}` : `Effacer les données de ${site.name}`}
        nomEtablissement={site.name}
        motExige={confirmation?.type === 'erase' ? 'EFFACER' : null}
        avertissement={confirmation?.type === 'close'
          ? "La clôture désactive l'établissement : plus aucune création, fin du service, début du délai de dépose du matériel. Elle n'efface rien, mais elle ne se rouvre pas depuis cet écran."
          : "Effacement définitif de toutes les données de l'établissement (résidents, chambres nommées, alertes, main courante, clips), puis émission d'un certificat scellé. Rien ne se récupère ensuite."}
        erreur={confirmation?.erreur}
        enCours={confirmation?.enCours}
        libelleBouton={confirmation?.type === 'close' ? 'Clore' : 'Effacer définitivement'}
        onAnnuler={() => setConfirmation(null)}
        onConfirmer={confirmer}
      />
    </Paper>
  );
}
